import { getWindowObject } from '@eventbrite/feature-detection';
import {
    checkGate,
    getExperiment,
    getLayer,
    StatsigUser,
} from '@eventbrite/statsig';
import { EventStatus } from '../constants/eventStatus';
import {
    ALWAYS_ENABLE_TICKETS_PAGE_BUTTON_LAYER_EXPERIMENT,
    EXPRESS_CHECKOUT_CTA_COPY_EXPERIMENT,
    OrderFormFormat,
    ORDER_FORM_SPLITTED_IN_TWO_PAGES_EXPERIMENT,
    PromoCodeFieldVisibilityCriteria,
    PROMO_CODE_EXPERIMENT,
    TicketsFormat,
    TicketsPageButtonEnabledCriteria,
    TICKETS_AS_INDIVIDUAL_CARDS_EXPERIMENT,
    TrustMessageText,
    TRUST_MESSAGE_EXPERIMENT,
    UPDATE_DATE_FORMAT_EXPERIMENT,
} from '../constants/statsig';
import { StatsigConf } from '../models/statsig';
import { hasSomeTicketWithMoreThanOneVariant } from './tickets';

export const getInitialStatsigCustomUserConfig = (
    app: StatsigConf,
): StatsigUser => {
    const { userAgent } = getWindowObject('navigator') ?? {};

    return {
        custom: {
            eventId: app.event?.id,
            isEventbriteTLD: app.isEventbriteTLD,
            ...(userAgent
                ? { userAgentLowerCase: userAgent.toLowerCase() }
                : {}),
            useAndroidProfile: app.useAndroidProfile,
            useIosProfile: app.useIosProfile,
            isFree: app.event?.is_free,
            isPaid: app.event?.properties?.is_paid,
            isLocked: app.event?.is_locked,
            isReservedSeating: app.event?.is_reserved_seating,
            isSeries: app.event?.is_series,
            isSeriesParent: app.event?.is_series_parent,
            listed: app.event?.listed,
            onlineEvent: app.event?.online_event,
            isDonation: app.event?.properties?.is_donation,
            hasDonationTicketsAvailable:
                app.event?.properties?.has_donation_tickets_available,
            isLive: app.event?.status === EventStatus.live,
            isRegistrationEvent: app.isRegistrationEvent,
            hasTicketResellers: !!app.event?.ticket_reseller,
            isWaitlist: !!app.event?.waitlist_settings,
            hasMultivariantTickets: hasSomeTicketWithMoreThanOneVariant(
                app.tickets,
            ),
            isUserLoggedIn: app.user?.isAuthenticated,
            hasScreenWidth: getWindowObject('document')?.body.clientWidth,
        },
        customIDs: {
            correlationId: app.request?.correlation_id,
            eventId: app.event?.id,
            organizerId: app.event?.organizer_id,
            sessionId: app.request?.session_id,
        },
    };
};

export const checkCardImprovementsFeatureGate = () =>
    checkGate('checkout_card_improvements');

export const getTrustMessageExperimentValue = () =>
    getExperiment(
        TRUST_MESSAGE_EXPERIMENT.name,
        TRUST_MESSAGE_EXPERIMENT.param,
        TRUST_MESSAGE_EXPERIMENT.defaultValue,
    );

export const isNoMessageVariant = () =>
    getTrustMessageExperimentValue() === TrustMessageText.NO_MESSAGE;

export const isTransactionSafeVariant = () =>
    getTrustMessageExperimentValue() ===
    TrustMessageText.VARIANT_TRANSACTION_SAFE;

export const getPromoCodeVisibilityExperiment = () =>
    getLayer(
        PROMO_CODE_EXPERIMENT.layer,
        PROMO_CODE_EXPERIMENT.param,
        PROMO_CODE_EXPERIMENT.defaultValue,
    );

export const isPromoCodeOpenByDefault = () =>
    getPromoCodeVisibilityExperiment() ===
    PromoCodeFieldVisibilityCriteria.always;

export const getAlwaysEnableTicketsPageButtonExperimentVariant = () =>
    getLayer<
        TicketsPageButtonEnabledCriteria,
        TicketsPageButtonEnabledCriteria
    >(
        ALWAYS_ENABLE_TICKETS_PAGE_BUTTON_LAYER_EXPERIMENT.layer,
        ALWAYS_ENABLE_TICKETS_PAGE_BUTTON_LAYER_EXPERIMENT.param,
        ALWAYS_ENABLE_TICKETS_PAGE_BUTTON_LAYER_EXPERIMENT.defaultValue,
    );

export const isUpdateFormatDateExperimentEnabled = () => {
    const value = getLayer(
        UPDATE_DATE_FORMAT_EXPERIMENT.layer,
        UPDATE_DATE_FORMAT_EXPERIMENT.param,
        UPDATE_DATE_FORMAT_EXPERIMENT.defaultValue,
    );

    return value === true || value === 'true';
};

export const getTicketsAsIndividualCardsExperimentVariant = () =>
    getLayer<TicketsFormat, TicketsFormat>(
        TICKETS_AS_INDIVIDUAL_CARDS_EXPERIMENT.layer,
        TICKETS_AS_INDIVIDUAL_CARDS_EXPERIMENT.param,
        TICKETS_AS_INDIVIDUAL_CARDS_EXPERIMENT.defaultValue,
    );

export const getOrderFormSplittedInTwoPagesExperimentVariant = () =>
    getLayer(
        ORDER_FORM_SPLITTED_IN_TWO_PAGES_EXPERIMENT.layer,
        ORDER_FORM_SPLITTED_IN_TWO_PAGES_EXPERIMENT.param,
        ORDER_FORM_SPLITTED_IN_TWO_PAGES_EXPERIMENT.defaultValue,
    );

export const isOrderFormSplittedInTwoPages = () =>
    getOrderFormSplittedInTwoPagesExperimentVariant() ===
    OrderFormFormat.twoPages;

export const checkoutMoveOrderFormFeatureGate = () =>
    checkGate('checkout_move_order_form_feature_gate');

export const isExpressCheckoutCtaCopyVariant = (): boolean => {
    const value = getLayer(
        EXPRESS_CHECKOUT_CTA_COPY_EXPERIMENT.layer,
        EXPRESS_CHECKOUT_CTA_COPY_EXPERIMENT.param,
        EXPRESS_CHECKOUT_CTA_COPY_EXPERIMENT.defaultValue,
    );

    return value === true || value === 'true';
};
